import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import {
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import CustomSelectDropdown from 'components/shared/DropDown/CustomSelectDropdown';
import ProjectCreatedModal from 'components/shared/modal/ProjectCreatedModal';
import BuyCreditsModal from 'components/shared/BuyCreditsModal';
import axiosPrivate from 'config/axiosPrivate';
import './SmartContentPopintrad.css';

const EMPTY_INITIAL_VALUES = {};

const TranslationPopin = ({
  isOpen,
  onClose,
  onSubmit,
  agents = [],
  initialValues = EMPTY_INITIAL_VALUES,
  disableGenerateButton = false,
  onBlockedGenerateClick,
}) => {
  const { t } = useTranslation();
  const history = useHistory();

  const [form, setForm] = useState({
    keyword: '',
    agentId: '',
    targetUrl: '',
  });
  const [remainingCredits, setRemainingCredits] = useState(null);
  const [canCreateContent, setCanCreateContent] = useState(true);
  const [isCreditsLimitModalOpen, setIsCreditsLimitModalOpen] = useState(false);
  const [isBuyCreditsModalOpen, setIsBuyCreditsModalOpen] = useState(false);
  const [isContentLimitModalOpen, setIsContentLimitModalOpen] = useState(false);
  const lastQuotaFetchKeyRef = useRef('');
  const quotaFetchInFlightRef = useRef(false);

  const normalizeUrl = (input) =>
    String(input || '')
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\//, '')
      .replace(/^www\./, '')
      .replace(/\/+$/, '');

  const checkWebsiteQuota = async (websiteValue) => {
    const normalizedWebsite = normalizeUrl(websiteValue);
    if (!normalizedWebsite) return true;
    try {
      const websitesResponse = await axiosPrivate.get('/project/website/all');
      const websites = Array.isArray(websitesResponse?.data) ? websitesResponse.data : [];
      const matchingWebsite = websites.find((websiteItem) => {
        const candidate = normalizeUrl(websiteItem?.urlSite || websiteItem?.url || websiteItem?.name || '');
        return candidate === normalizedWebsite;
      });
      if (!matchingWebsite?.id) return true;

      const quotasResponse = await axiosPrivate.get(`/project/website-quotas/${matchingWebsite.id}`);
      const maxTexts = Number(quotasResponse?.data?.maxTexts ?? 0);
      const usedTexts = Number(quotasResponse?.data?.usedTexts ?? 0);
      return maxTexts - usedTexts > 0;
    } catch {
      return true;
    }
  };

  const fetchQuotaStatus = async (websiteValue) => {
    const normalizedWebsite = normalizeUrl(websiteValue);
    const fetchKey = `${isOpen ? 'open' : 'closed'}:${normalizedWebsite}`;

    if (lastQuotaFetchKeyRef.current === fetchKey || quotaFetchInFlightRef.current) {
      return;
    }
    lastQuotaFetchKeyRef.current = fetchKey;
    quotaFetchInFlightRef.current = true;

    try {
      const [{ data }, websiteQuotaAllowed] = await Promise.all([
        axiosPrivate.get('/project/company-quotas'),
        checkWebsiteQuota(websiteValue),
      ]);
      setCanCreateContent(websiteQuotaAllowed);
      const totalCredits = Number(data?.totalCredits ?? 0);
      const usedCredits = Number(data?.usedCredits ?? 0);
      if (!Number.isNaN(totalCredits) && !Number.isNaN(usedCredits)) {
        setRemainingCredits(Math.max(totalCredits - usedCredits, 0));
      } else {
        setRemainingCredits(null);
      }
    } catch {
      setCanCreateContent(true);
      setRemainingCredits(null);
    } finally {
      quotaFetchInFlightRef.current = false;
    }
  };

  useEffect(() => {
    if (!isOpen) {
      lastQuotaFetchKeyRef.current = '';
      setForm({
        keyword: '',
        agentId: '',
        targetUrl: '',
      });
      return;
    }

    if (initialValues) {
      setForm((prev) => ({
        ...prev,
        ...initialValues,
      }));
    }

    let websiteValue = '';
    try {
      const raw = window.localStorage.getItem('AC_Dashboard_SelectedWebsite');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          websiteValue = parsed?.urlSite || parsed?.url || parsed?.name || String(raw);
        } catch {
          websiteValue = raw;
        }
      }
    } catch {
      websiteValue = '';
    }

    fetchQuotaStatus(websiteValue);
  }, [isOpen, initialValues]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canCreateContent) {
      onClose?.();
      setIsContentLimitModalOpen(true);
      return;
    }
    if (remainingCredits !== null && remainingCredits <= 0) {
      if (typeof onBlockedGenerateClick === 'function') {
        onBlockedGenerateClick();
        return;
      }
      onClose?.();
      setIsCreditsLimitModalOpen(true);
      return;
    }
    if (disableGenerateButton === true) {
      if (typeof onBlockedGenerateClick === 'function') {
        onBlockedGenerateClick();
        return;
      }
      setIsCreditsLimitModalOpen(true);
      return;
    }
    if (onSubmit) {
      onSubmit(form);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={onClose}
        size="md"
        className="translation-popin"
      >
      <ModalHeader className="modal-title" toggle={onClose}>
        {t('Traduire un nouveau contenu')}
      </ModalHeader>
      <Form onSubmit={handleSubmit}>
        <ModalBody>
          {/* Section 1 — Informations requises */}
          <div className="smart-content-popin-section">
            <h6 className="smart-content-popin-section-title">
              <img
                src="/Images/info-glob.png"
                alt="info"
                className="smart-content-popin-info-icon"
              />
              {t('Section 1 — Informations requises')}
            </h6>
            <FormGroup>
              <Label
                htmlFor="translationKeyword"
                className="smart-content-popin-label"
              >
                {t('Mot-clé principal')}{' '}
                <span className="smart-content-popin-required">*</span>
              </Label>
              <Input
                id="translationKeyword"
                type="text"
                placeholder={t('Ex: télépéage automatique')}
                value={form.keyword}
                onChange={handleChange('keyword')}
                className="keyword-input smart-content-popin-input"
              />
           
            </FormGroup>
          </div>

          {/* Section 2 — Traduction IA */}
          <div className="smart-content-popin-section">
            <h6 className="smart-content-popin-section-title">
              <img
                src="/Images/ai-popin-icon.png"
                alt="info"
                className="smart-content-popin-info-icon"
              />
              {t('Section 2 — Traduction IA')}
            </h6>

            <FormGroup>
              <Label
                htmlFor="translationAgent"
                className="smart-content-popin-label"
              >
                {t('Agent IA')}
              </Label>
              <CustomSelectDropdown
                options={agents || []}
                value={
                  agents.find(
                    (a) =>
                      String(a.id || a.agentId) === String(form.agentId || '')
                  ) || null
                }
                onChange={(selected) => {
                  setForm((prev) => ({
                    ...prev,
                    agentId: selected
                      ? selected.id || selected.agentId || ''
                      : '',
                  }));
                }}
                getOptionLabel={(agent) =>
                  agent?.name || agent?.agentName || t('Agent IA')
                }
                renderOptionLabel={(agent, isSelectedDisplay) => {
                  if (!agent) return t('Agent IA');
                  const name = agent.name || agent.agentName || '';
                  const lang =
                    agent.language ||
                    agent.langue ||
                    agent.lang ||
                    agent.code ||
                    '';

                  if (isSelectedDisplay) {
                    // Affichage dans l'input sélectionné : agent à gauche, langue à droite
                    return (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        <span>{name}</span>
                        {lang && (
                          <span
                            style={{
                              color: '#4B5563',
                              fontSize: 12,
                              marginLeft: 'auto',
                            }}
                          >
                            {lang}
                          </span>
                        )}
                      </span>
                    );
                  }

                  // Affichage dans la liste du dropdown : "Agent - Langue"
                  if (lang) {
                    return (
                      <span>
                        {name} - <span style={{ color: '#4B5563' }}>{lang}</span>
                      </span>
                    );
                  }

                  return <span>{name}</span>;
                }}
                getOptionValue={(agent) => agent?.id || agent?.agentId}
                placeholder={t('Sélectionnez un agent IA')}
                className="smart-content-popin-input"
              />
            </FormGroup>

          </div>

          {/* Section 3 — Paramètres SEO avancés (optionnel) */}
          <div className="smart-content-popin-section">
            <h6 className="smart-content-popin-section-title-seo">
              <img
                src="/Images/seo-settings.png"
                alt="info"
                className="smart-content-popin-info-icon"
              />
              {t('Section 3 — Paramètres SEO avancés (optionnel)')}
            </h6>

            <FormGroup>
              <Label
                htmlFor="translationTargetUrl"
                className="smart-content-popin-label"
              >
                {t('URL cible')}
              </Label>
              <Input
                id="translationTargetUrl"
                type="url"
                placeholder={t('Ex: https://www.exemple.com/page-cible')}
                value={form.targetUrl}
                onChange={handleChange('targetUrl')}
                className="smart-content-popin-input"
              />
            </FormGroup>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            type="button"
            onClick={onClose}
            className="smart-content-popin-cancel"
          >
            {t('Annuler')}
          </Button>
          <Button
            type="submit"
            className="smart-content-popin-button"
            disabled={!form.keyword?.trim() || !form.agentId}
          >
            {t('Continuer')}
          </Button>
        </ModalFooter>
      </Form>
      </Modal>
      <ProjectCreatedModal
        isOpen={isContentLimitModalOpen}
        onClose={() => setIsContentLimitModalOpen(false)}
        title={t('Limite de contenus atteinte')}
        message={t("Vous avez atteint le nombre maximum de contenus inclus dans votre forfait. Passez à une offre supérieure pour continuer à créer et optimiser vos contenus.")}
        confirmButtonText={t('Passer au forfait supérieur')}
        onConfirm={() => {
          setIsContentLimitModalOpen(false);
          history.push('/AdminClient/Abonnement');
        }}
        secondaryButtonText={t('Fermer')}
        onSecondaryAction={() => {
          setIsContentLimitModalOpen(false);
        }}
      />
      <ProjectCreatedModal
        isOpen={isCreditsLimitModalOpen}
        onClose={() => setIsCreditsLimitModalOpen(false)}
        title={t('Limite atteinte')}
        message={t("Vous avez atteint la limite autorisée par votre forfait. Rechargez votre compte dès maintenant pour continuer à générer du contenu, enrichir vos textes et utiliser vos agents sans interruption.")}
        confirmButtonText={t('Acheter des crédits')}
        onConfirm={() => {
          setIsCreditsLimitModalOpen(false);
          setIsBuyCreditsModalOpen(true);
        }}
        secondaryButtonText={t('Passer à une offre supérieure')}
        onSecondaryAction={() => {
          setIsCreditsLimitModalOpen(false);
          history.push('/AdminClient/Abonnement');
        }}
      />
      <BuyCreditsModal open={isBuyCreditsModalOpen} onClose={() => setIsBuyCreditsModalOpen(false)} />
    </>
  );
};

export default TranslationPopin;

